import { useCallback } from "react";
import { Outlet, Route, Routes } from "react-router";
import { MENU_ITEMS } from "./Menu.constants";
import { ModuleSwitchItem } from "./MenuItem/ModuleSwitchItem";
import type { MenuRouteProps } from "./MenuRoute/MenuRoute";
import { MenuRoute } from "./MenuRoute/MenuRoute";

interface MenuProps {
  isStaff?: boolean;
  adminUrl?: string;
  userHasAnalyzerLicense: boolean;
}

function Menu({ isStaff, adminUrl, userHasAnalyzerLicense }: MenuProps) {
  const MenuRouteWithCommonProps = useCallback(
    (props: Omit<MenuRouteProps, "isStaff" | "adminUrl">) => {
      return <MenuRoute adminUrl={adminUrl} isStaff={isStaff} {...props} />;
    },
    [isStaff, adminUrl]
  );

  const ProductSelectPage = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.PRODUCT_SELECT_PAGE}
    />
  );
  const ManagerStartPage = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.MANAGER_START_PAGE}
    />
  );
  const ManagerProjectList = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.MANAGER_PROJECT_LIST}
    />
  );
  const ManagerProjectView = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.MANAGER_PROJECT_VIEW}
    />
  );
  const ManagerVariantStructure = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.MANAGER_VARIANT_STRUCTURE}
    />
  );
  const Todos = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.TODOS} />
  );
  const Onboarding = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.ONBOARDING} />
  );
  const DeliveryOverview = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.DELIVERY_OVERVIEW} />
  );
  const GuaranteeOfOrigin = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.GUARANTEE_OF_ORIGIN}
    />
  );
  const EnergyData = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.ENERGY_DATA} />
  );
  const DirektvermarktungProjectLevel = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.VERMARKTUNG_PROJECT_LEVEL}
    />
  );
  const Paragraph6 = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.PARAGRAPH6} />
  );
  const RevenueMonitoringProjectLevel = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.REVENUE_MONITORING_PROJECT_LEVEL}
    />
  );
  const ApiSettings = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.API_SETTINGS} />
  );
  const Direktvermarktung = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.VERMARKTUNG} />
  );
  const RevenueMonitoring = (
    <MenuRouteWithCommonProps
      menuItemForRoute={MENU_ITEMS.REVENUE_MONITORING}
    />
  );
  const ElementModuleSwitchItemManager = (
    <ModuleSwitchItem
      isManagerPage
      userHasAnalyzerLicense={userHasAnalyzerLicense}
    />
  );
  const PPAInvoicing = (
    <MenuRouteWithCommonProps menuItemForRoute={MENU_ITEMS.PPA_INVOICING} />
  );

  return (
    <>
      <Routes>
        <Route path="/">
          <Route element={ProductSelectPage} index />
          <Route element={ManagerStartPage} path="manager/startseite" />
          <Route path="manager/projekte">
            <Route element={ManagerProjectList} index />
            <Route element={<Outlet />} path=":projectId">
              <Route element={ManagerProjectView} path=":projectPage">
                <Route element={ManagerProjectView} index />
                <Route element={ManagerProjectView} path="*" />
              </Route>
              <Route
                element={ManagerVariantStructure}
                path="liegenschaften/:siteId"
              >
                <Route element={ManagerVariantStructure} index />
                <Route element={ManagerVariantStructure} path="*" />
              </Route>
              <Route element={Todos} path="aufgaben">
                <Route element={Todos} index />
                <Route element={Todos} path="*" />
              </Route>
              <Route element={Onboarding} path="onboarding">
                <Route element={Onboarding} index />
                <Route element={Onboarding} path="*" />
              </Route>
              <Route element={DeliveryOverview} path="lieferungen-uebersicht">
                <Route element={DeliveryOverview} index />
                <Route element={DeliveryOverview} path="*" />
              </Route>
              <Route element={GuaranteeOfOrigin} path="herkunftsnachweis">
                <Route element={GuaranteeOfOrigin} index />
                <Route element={GuaranteeOfOrigin} path="*" />
              </Route>
              <Route element={EnergyData} path="energiedaten">
                <Route element={EnergyData} index />
                <Route element={EnergyData} path="*" />
              </Route>
              <Route element={DirektvermarktungProjectLevel} path="vermarktung">
                <Route element={DirektvermarktungProjectLevel} index />
                <Route element={DirektvermarktungProjectLevel} path="*" />
              </Route>
              <Route element={Paragraph6} path="paragraph6">
                <Route element={Paragraph6} index />
                <Route element={Paragraph6} path="*" />
              </Route>
              <Route
                element={RevenueMonitoringProjectLevel}
                path="erloesmonitoring"
              >
                <Route element={RevenueMonitoringProjectLevel} index />
                <Route element={RevenueMonitoringProjectLevel} path="*" />
              </Route>
            </Route>
          </Route>
          <Route element={<Outlet />} path="manager/liegenschaften">
            <Route element={<Outlet />} index />
            <Route element={<Outlet />} path="*" />
          </Route>
          <Route element={ApiSettings} path="api-einstellungen/:projectId?" />
          <Route element={ProductSelectPage} path="einstellungen" />
        </Route>
        <Route element={Direktvermarktung} path="manager/vermarktung">
          <Route element={Direktvermarktung} index />
          <Route element={Direktvermarktung} path="*" />
        </Route>
        <Route element={RevenueMonitoring} path="manager/erloesmonitoring">
          <Route element={RevenueMonitoring} index />
          <Route element={RevenueMonitoring} path="*" />
        </Route>
        <Route element={PPAInvoicing} path="manager/ppa-abrechnung">
          <Route element={PPAInvoicing} index />
          <Route element={PPAInvoicing} path="*" />
        </Route>
      </Routes>
      <Routes>
        <Route element={<Outlet />} path="/">
          <Route path="manager/projekte">
            <Route element={<Outlet />} index />
            <Route element={ElementModuleSwitchItemManager} path="*" />
          </Route>
          <Route element={<Outlet />} path="*" />
        </Route>
      </Routes>
    </>
  );
}

export { Menu, MenuProps };
